import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articleDetails } from '@/mocks/articleData';
import type { ArticleDetail } from '@/mocks/articleData';

const articleImages: Record<string, string> = {
  'hot-route-1': 'https://readdy.ai/api/search-image?query=Luxury%20overwater%20villa%20at%20sunset%20on%20a%20tropical%20island%2C%20private%20infinity%20pool%20overlooking%20calm%20turquoise%20ocean%2C%20wooden%20deck%20with%20candlelit%20dinner%20setup%20for%20two%2C%20warm%20golden%20hour%20light%2C%20romantic%20and%20exclusive%20atmosphere%2C%20luxury%20travel%20photography&width=1200&height=600&seq=detail-sunset-3day&orientation=landscape',
  'hot-route-2': 'https://readdy.ai/api/search-image?query=Couple%20snorkeling%20in%20crystal%20clear%20tropical%20water%20with%20colorful%20coral%20reef%20and%20tropical%20fish%2C%20sunlight%20beams%20penetrating%20through%20water%20surface%2C%20vibrant%20underwater%20scene%2C%20paradise%20island%20view%2C%20professional%20underwater%20photography&width=1200&height=600&seq=detail-island-5day&orientation=landscape',
  'hot-route-3': 'https://readdy.ai/api/search-image?query=Elegant%20beach%20proposal%20setup%20at%20sunset%20with%20thousands%20of%20rose%20petals%2C%20fairy%20lights%20wrapped%20around%20palm%20trees%2C%20diamond%20ring%20in%20foreground%2C%20champagne%20glasses%2C%20ocean%20horizon%20with%20golden%20sunset%2C%20ultra%20romantic%20atmosphere%2C%20cinematic%20photography&width=1200&height=600&seq=detail-proposal-4day&orientation=landscape',
  'hot-route-4': 'https://readdy.ai/api/search-image?query=Convertible%20car%20driving%20along%20scenic%20coastal%20road%20on%20tropical%20island%2C%20turquoise%20ocean%20on%20one%20side%2C%20lush%20green%20hills%20on%20the%20other%2C%20bright%20sunny%20day%2C%20sense%20of%20freedom%20and%20adventure%2C%20road%20trip%20travel%20photography&width=1200&height=600&seq=detail-free-7day&orientation=landscape',
  'article-season': 'https://readdy.ai/api/search-image?query=Tropical%20island%20landscape%20showing%20different%20seasons%20blended%20together%2C%20sunny%20blue%20sky%20with%20lush%20green%20palm%20trees%2C%20turquoise%20ocean%20water%2C%20travel%20magazine%20cover%20style%2C%20warm%20and%20inviting%20atmosphere%2C%20professional%20landscape%20photography&width=1200&height=600&seq=detail-season-guide&orientation=landscape',
  'article-sunset': 'https://readdy.ai/api/search-image?query=Breathtaking%20tropical%20sunset%20over%20calm%20ocean%20with%20vibrant%20orange%20pink%20and%20purple%20sky%2C%20silhouette%20of%20palm%20trees%20and%20rock%20formations%2C%20reflection%20on%20water%20surface%2C%20serene%20and%20romantic%20mood%2C%20fine%20art%20nature%20photography&width=1200&height=600&seq=detail-sunset-spots&orientation=landscape',
};

const categoryNames: Record<string, string> = {
  honeymoon: '蜜月行程',
  couple: '情侣度假',
  proposal: '求婚之旅',
  'free-travel': '自由行线路',
  'group-tour': '跟团游',
  photo: '旅拍攻略',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleDetail | null>(null);

  useEffect(() => {
    if (slug && articleDetails[slug]) {
      setArticle(articleDetails[slug]);
    }
  }, [slug]);

  if (!article) {
    return (
      <>
        <header><Navbar /></header>
        <main className="pt-16 md:pt-18">
          <div className="container-wide py-20 text-center">
            <p className="text-foreground-500 text-sm mb-4">文章未找到</p>
            <Link to="/" className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryPath = `/category/${article.categorySlug}`;
  const categoryName = categoryNames[article.categorySlug] || article.category;
  const imageUrl = articleImages[article.imageSeq] || articleImages['hot-route-1'];

  const relatedArticles = article.relatedArticles
    .map((slug) => articleDetails[slug])
    .filter(Boolean);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: imageUrl,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <header><Navbar /></header>

      <main className="pt-16 md:pt-18">
        <article>
          <div className="relative w-full h-64 md:h-80 lg:h-96 overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('${imageUrl}')` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-black/30"></div>
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8">
              <div className="container-wide">
                <div className="flex items-center gap-2 mb-3">
                  <Link to={categoryPath} className="px-2.5 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full">
                    {categoryName}
                  </Link>
                  <span className="text-xs text-background-50/70">{article.date}</span>
                </div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold font-heading text-background-50 max-w-3xl">
                  {article.title}
                </h1>
              </div>
            </div>
          </div>

          <div className="container-wide py-6 md:py-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName, path: categoryPath },
                { label: article.title },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
              <div className="flex-1 max-w-3xl">
                <div className="flex items-center gap-4 pb-6 mb-6 border-b border-background-200 text-xs text-foreground-500">
                  <span>作者：{article.author}</span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-time-line text-[10px]"></i>
                    </span>
                    阅读时间 {article.readTime}
                  </span>
                </div>

                <div
                  className="prose-content text-sm md:text-base leading-relaxed text-foreground-700"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
                  <span className="text-xs text-foreground-500 font-medium">标签：</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                {relatedArticles.length > 0 && (
                  <div className="mt-10 pt-8 border-t border-background-200">
                    <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">相关推荐</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {relatedArticles.map((rel) => (
                        <Link
                          key={rel.slug}
                          to={`/article/${rel.slug}`}
                          className="flex items-start gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors group"
                        >
                          <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                            <div
                              className="w-full h-full bg-cover bg-center"
                              style={{ backgroundImage: `url('${articleImages[rel.imageSeq] || articleImages['hot-route-1']}')` }}
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                              {rel.title}
                            </p>
                            <p className="text-xs text-foreground-500 mt-1">{rel.date} · {rel.readTime}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <aside className="lg:w-72 flex-shrink-0">
                <div className="sticky top-24">
                  <div className="bg-background-100 rounded-xl p-5 mb-5">
                    <h4 className="text-sm font-bold font-heading text-foreground-900 mb-3 flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-heart-line text-sm text-primary-500"></i>
                      </span>
                      关于作者
                    </h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {article.author}是爱情岛线路的资深旅行顾问，拥有多年海岛旅行策划经验，致力于为每一对情侣打造最浪漫的旅行回忆。
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-xl p-5">
                    <h4 className="text-sm font-bold font-heading text-foreground-900 mb-3">热门标签</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {article.tags.map((tag) => (
                        <span key={tag} className="px-2.5 py-1 bg-background-200 text-foreground-600 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}